import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { selCls } from "../hooks/useMasterMaps";
import { toast } from "sonner";
import { Plus, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

export function ScrStatusBadge({ status }) {
  const map = {
    Draft: "text-[#6B7280] border-gray-300 bg-gray-50",
    Pending: "text-[#D97706] border-[#D97706]/30 bg-[#D97706]/5",
    Approved: "text-[#1B7F3B] border-[#1B7F3B]/30 bg-[#1B7F3B]/5",
    Rejected: "text-[#BB162B] border-[#BB162B]/30 bg-[#BB162B]/5",
    Withdrawn: "text-[#6B7280] border-gray-300 bg-gray-50",
  };
  return <span className={`inline-block border px-2 py-0.5 text-[11px] font-medium rounded-sm ${map[status]}`}>{status}</span>;
}

export function TrailView({ scr }) {
  const stages = ["Branch Head", "VP Vertical", "HR Head", "AMD"];
  const trail = scr.approval_trail || [];
  return (
    <div className="space-y-2">
      {stages.map((stage, i) => {
        const entry = trail.find((t) => t.stage === stage);
        const isPending = scr.status === "Pending" && !entry && trail.filter((t) => t.action === "Approved").length === i;
        return (
          <div key={stage} className={`flex items-start gap-3 p-3 border rounded-sm ${entry ? (entry.action === "Approved" ? "border-[#1B7F3B]/30 bg-[#1B7F3B]/5" : "border-[#BB162B]/30 bg-[#BB162B]/5") : isPending ? "border-[#D97706]/40 bg-[#D97706]/5" : "border-gray-200"}`}>
            <div className="text-xs font-semibold text-[#0E2240] w-24 shrink-0">{stage}</div>
            {entry ? (
              <div className="text-xs">
                <span className={entry.action === "Approved" ? "text-[#1B7F3B] font-medium" : "text-[#BB162B] font-medium"}>{entry.action}</span>
                <span className="text-[#6B7280]"> by {entry.actor_name} · {new Date(entry.acted_on).toLocaleDateString("en-IN")}</span>
                {entry.comments && <div className="text-[#6B7280] mt-0.5">"{entry.comments}"</div>}
              </div>
            ) : (
              <div className="text-xs text-[#6B7280]">{isPending ? "Awaiting action — currently stuck here" : "Not yet reached"}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const inr = (v) => (v ? `₹${Number(v).toLocaleString("en-IN")}` : "—");

export default function ScrRegister() {
  const { can, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [fy, setFy] = useState("");
  const [viewing, setViewing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/scr").then(({ data }) => setRows(data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const fys = useMemo(() => [...new Set(rows.map((r) => r.fy))].sort().reverse(), [rows]);
  const filtered = rows.filter((r) => (!status || r.status === status) && (!fy || r.fy === fy));

  const withdraw = async (r) => {
    try {
      await api.post(`/scr/${r.scr_id}/withdraw`);
      toast.success("SCR withdrawn");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submit = async (r) => {
    try {
      await api.post(`/scr/${r.scr_id}/submit`);
      toast.success("SCR submitted for approval");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="scr-register-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Sanction Change Register</h1>
          <p className="text-sm text-[#6B7280] mt-1">Every sanction change — who, when, why and cost impact</p>
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="scr-fy-filter" className={selCls} value={fy} onChange={(e) => setFy(e.target.value)}>
            <option value="">All FYs</option>
            {fys.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select data-testid="scr-status-filter" className={selCls} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {["Draft", "Pending", "Approved", "Rejected", "Withdrawn"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {can("scr", "create") && (
            <Link to="/scr/new" data-testid="raise-scr-button"
              className="flex items-center gap-2 bg-[#0E2240] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#1a3358] transition-colors duration-200">
              <Plus size={15} /> Raise SCR
            </Link>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="scr-table">
            <thead>
              <tr className="border-b border-gray-200 bg-[#F5F6F8]">
                {["SCR No", "Branch", "Department", "Designation", "Change", "Monthly Cost", "Requested By", "Status", "Stuck At", ""].map((h, i) => (
                  <th key={i} className="px-3 py-3 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-[#6B7280]">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-[#6B7280]">No change requests</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.scr_id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs font-medium text-[#0E2240]">{r.scr_no}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.branch_name}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.dept_name}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.desig_name}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">
                    {r.current_count} → <span className="font-semibold">{r.proposed_count}</span>
                    <span className={`ml-1.5 text-xs font-medium ${r.delta > 0 ? "text-[#1B7F3B]" : "text-[#BB162B]"}`}>
                      ({r.delta > 0 ? "+" : ""}{r.delta})
                    </span>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap tabular-nums text-right">{inr(r.monthly_cost_impact)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs">{r.requested_by_name}<div className="text-[#6B7280]">{new Date(r.requested_on).toLocaleDateString("en-IN")}</div></td>
                  <td className="px-3 py-2.5"><ScrStatusBadge status={r.status} /></td>
                  <td className="px-3 py-2.5 text-xs text-[#D97706] whitespace-nowrap">{r.pending_stage || "—"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-right">
                    <button data-testid={`scr-view-${r.scr_id}`} onClick={() => setViewing(r)} className="text-[#1E5AA8] hover:text-[#0E2240] p-1" aria-label="View">
                      <Eye size={15} />
                    </button>
                    {r.status === "Draft" && r.requested_by_id === user?.user_id && (
                      <button data-testid={`scr-submit-${r.scr_id}`} onClick={() => submit(r)} className="ml-1 text-xs text-[#1B7F3B] hover:underline">Submit</button>
                    )}
                    {["Draft", "Pending"].includes(r.status) && r.requested_by_id === user?.user_id && (
                      <button data-testid={`scr-withdraw-${r.scr_id}`} onClick={() => withdraw(r)} className="ml-2 text-xs text-[#BB162B] hover:underline">Withdraw</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-200 text-xs text-[#6B7280] tabular-nums">{filtered.length} change requests</div>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-lg max-h-[85vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-[#0E2240]">{viewing.scr_no}</DialogTitle>
                <DialogDescription className="text-xs">
                  {viewing.branch_name} · {viewing.dept_name} · {viewing.desig_name}
                </DialogDescription>
              </DialogHeader>
              <div className="text-sm space-y-2">
                <div className="tabular-nums"><span className="text-[#6B7280]">Change:</span> {viewing.current_count} → <b>{viewing.proposed_count}</b> ({viewing.delta > 0 ? "+" : ""}{viewing.delta})</div>
                <div><span className="text-[#6B7280]">Driver:</span> {viewing.business_driver || "—"}</div>
                <div className="tabular-nums"><span className="text-[#6B7280]">Monthly cost impact:</span> {inr(viewing.monthly_cost_impact)}</div>
                <div><span className="text-[#6B7280]">Justification:</span> {viewing.justification}</div>
                {viewing.effective_from && <div><span className="text-[#6B7280]">Effective from:</span> {viewing.effective_from}</div>}
              </div>
              <div className="mt-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2">Approval trail</div>
                <TrailView scr={viewing} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
